// exceptions
import { CountryError } from "shared/exceptions";

// functions
import { getBoxStatus } from "shared/functions";

import { ProductController, UserController } from "controllers";

import Log from "models/log";

const timestamp = +new Date();
const role = localStorage.getItem('role');

export async function getTheProduct(productId, country) {
    const result = await ProductController.getProduct(productId);

    if (!result) {
        throw new Error("Invalid barcode");
    }
    if (result.market && result.market !== country) {
        throw new CountryError();
    }

    return result;
}

export async function getTheProductLogs(product) {
    const logsResults = await ProductController.getProductLogs(product.barcode);
    return logsResults.map((result) => new Log(result));
}

/**
 *
 * @param {*} barcode
 * @returns an exception if the barcode exists in the database
 */
export async function verifyBarcode(barcode) {
    const isNew = await ProductController.barcodeIsNew(barcode);
    if (!isNew) {
        throw new Error('This barcode is already taken.');
    }
}

export async function saveProductChanges(product, pristineProduct) {
    let success = false;
    let pro = JSON.parse(JSON.stringify(product));
    try {
        await ProductController.editProduct(product.barcode, pro);

        // if name, images or description changes
        // [0, 1, 2] are indices
        const imagesChanged = [0, 1, 2].some(
            (index) => product.imageURLs[index] !== pristineProduct.imageURLs[index]
        );

        if (
            product.name !== pristineProduct.name ||
            imagesChanged ||
            product.description !== pristineProduct.description
        ) {
            await editItemInBox(product, pristineProduct);
            await updateOtherCollections(product);
        }

        success = true;
    } catch (error) {
        console.error(error);
    }
    return success;
}

export async function recreateCurrentProduct(product, pristineProduct) {
    let success = false;

    try {

        await ProductController.create(product.toJSON());
        for (const log of product.logs) {
            await ProductController.createProductLog(
                product.barcode,
                log.boxID,
                log.toJSON()
            );
        }

        // reports
        product.reports = await ProductController.getReportsByProductBarcode(pristineProduct.barcode);

        for (const report of product.reports) {
            report.barcode = product.barcode;
            const payload = { ...report };
            delete payload.id;
            await ProductController.setRepairsReport(payload);
        }

        await editItemInBox(product, pristineProduct);
        if (product.imageURLs !== pristineProduct.imageURLs) {
            await updateOtherCollections(product);
        }

        // this loops through logs to get the boxID (which is == to document ID)
        for (const log of product.logs) {
            await ProductController.deleteProductLog(
                pristineProduct.barcode,
                log.boxID
            );
        }
        for (const report of product.reports) {

            await ProductController.deleteProductReport(
                pristineProduct.barcode,
                report.id
            );
        }

        await ProductController.deleteProduct(pristineProduct.barcode);

        success = true;
    } catch (error) {
        console.error(error);
    }
    return success;
}

async function updateOtherCollections(product) {
    const otherCollectionsToUpdate = [
        "liked_items",
        "unliked_items",
        "too_big",
        "too_small",
        "kept1month",
        "kept6months",
    ];

    for (const log of product.logs) {
        const { boxID, userID } = log;
        UserController.getUserBoxItemByProductBarcode(
            userID,
            boxID,
            product.barcode
        )
            .then((result) => {
                if (!result) {
                    return;
                }
                const promises = [];
                for (const collection of otherCollectionsToUpdate) {
                    promises.push(
                        UserController.getUserFeedbackCollections(
                            userID,
                            collection,
                            result.id
                        )
                    );
                }

                return Promise.all(promises);
            })
            .then((results) => {
                const promises = [];

                const collections = results.filter((result) => Boolean(result));
                for (const collection of collections) {
                    const payload = { imageURL: product.imageURLs };
                    promises.push(
                        UserController.setUserFeedbackCollections(
                            userID,
                            collection.collection_name,
                            collection.id,
                            payload
                        )
                    );
                }

                return Promise.all(promises);
            })
            .catch((error) => {
                console.error(error);
            });
    }
}

async function editItemInBox(product, pristineProduct) {
    const searchBarcode = pristineProduct.barcode;

    for (const log of product.logs) {
        const { boxID, userID } = log;

        const payload = {
            barcode: product.barcode,
            name: product.name,
            description: product.name,
            imageURL: product.imageURLs[0],
        };

        UserController.getUserBoxItemByProductBarcode(userID, boxID, searchBarcode)
            .then((result) => {
                const item = result;

                return item ? UserController.setUserBoxItem(userID, boxID, item.id, payload) : null;
            })
            .catch((error) => {
                console.error(error);
            });
    }
}

export const archiveProduct = async (barcode, reason = "damaged") => {
    let success = false
    try {

        if (window.confirm(`Are you sure you would like to archive ${barcode}?`)) {

            const product = await ProductController.getBarcodeById(barcode);

            const payload = { status: "archived", archived: { reason, from: role, at: timestamp } }
            //  if currentUser and currentBox, find box and items
            if (product.currentUser && product.currentBox) {
                //  change status of that item
                await UserController.updateUserBoxItemByBarcode({ userId: product.currentUser, boxId: product.currentBox, barcode, payload: { status: "archived" } });

                // update the status of the box
                const items = await UserController.getUserBoxItems(product.currentUser, product.currentBox);
                const boxStatus = await getBoxStatus(items);
                await ProductController.updateBoxInfo(product.currentUser, product.currentBox, { status: boxStatus });

                payload.currentBox = null;
                payload.currentUser = null;
            }

            // archive the product
            await ProductController.editProduct(barcode, payload);
            success = true;
        }
    } catch (error) {
        console.error(error);
    }
    return success;
}


